import {
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { mimChatbotsApi } from 'apis/chatbots';
import { ChatbotCreation } from 'app/views/layout/views/settings/views/chatbots/components/components/chatbotCreation';
import { ChatbotIdentity } from 'app/views/layout/views/settings/views/chatbots/components/components/chatbotIdentity/chatbotIdentity';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './chatbotCreateModal.module.scss';

const formId = 'mim-chatbot-create';

type FieldValues = {
  name: string;
  apiKey: string;
  token: string;
  userName: string;
  userAvatarUrl: string;
};

export const ChatbotCreateModal = createAppModal(
  { type: 'settings:createChatbotModal' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const [createChatbot, { isLoading: isCreating }] =
      mimChatbotsApi.useCreateMimChatbotMutation();
    const numberOfChatbots = modal.arg.numberOfChatbots;

    const organizationId = useAppSelector(
      (state) => state.settings.organization.organization.id,
    );

    const navigate = useAppNavigate();
    const notify = useNotifications();

    const form = useForm<FieldValues>({
      defaultValues: {
        name: '',
        apiKey: '',
        token: '',
        userName: '',
        userAvatarUrl: '',
      },
      mode: 'all',
    });

    const onSubmit: SubmitHandler<FieldValues> = async ({
      name,
      userName,
      userAvatarUrl,
      token,
      apiKey,
    }) => {
      try {
        const response = await createChatbot({
          organizationId,
          name,
          token,
          apiKey,
          user: {
            name: userName,
            avatarUrl: userAvatarUrl,
          },
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.name || 'Chatbot'}" has been created.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
          ampli.chatbotCreated({
            numberOfChatbots: numberOfChatbots + 1,
            chatbotType: response.data._type,
          });
          hide?.();
          navigate({ path: '/settings/chatbots/mim' });
        }
      } catch (error) {
        notify(
          {
            title: 'Error while creating chatbot.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Create chatbot" onClose={hide}></ModalHead>
        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormProvider {...form}>
              <Text.Paragraph variant="1" wrap="nowrap" withMarginBottom>
                1. Identify your chatbot
              </Text.Paragraph>
              <ChatbotIdentity />
              <Text.Paragraph variant="1" wrap="nowrap" withMarginBottom>
                2. Connect your Elevio account
              </Text.Paragraph>
              <ChatbotCreation />
            </FormProvider>
          </form>
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName="Create chatbot"
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isCreating,
            loadingIndicator: 'Creating...',
            type: 'submit',
            form: formId,
            disabled:
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
